import { Dialog } from "@headlessui/react"
import React, { useEffect, useState } from "react"
import { KButton, KSpan } from "kahuna-base-react-components"
import { users } from "../../../../../apis"
import { UserTakedown, UserTakedownStatusLog } from "../../../../../types"
import { mapListToCamelCase } from "../../../../../utility"
import { toast } from "react-toastify"
import { errorFilledToastOptions } from "../../../../../constants"
import { formatTakedownStatusLogDate, getTakedownProcessStatusLabel } from "../takedownProcessStatus"

const RESULT_COLORS: Record<string, string> = { success: "#45D865", partial_failed: "#F5A524", failed: "#E52B2B" }
const RESULT_LABELS: Record<string, string> = { partial_failed: "Partial Failed", failed: "Failed" }

export const getTakedownStatusLogColor = (statusLog?: UserTakedownStatusLog) => {
  return statusLog?.result ? RESULT_COLORS[statusLog.result] || "" : ""
}

export const TakedownProgressBar = ({ statusLog }: { statusLog?: UserTakedownStatusLog }) => {
  const percent = Math.max(0, Math.min(100, Math.round(statusLog?.progress || 0)))
  const color = getTakedownStatusLogColor(statusLog) || (percent === 100 ? RESULT_COLORS.success : "")
  const resultLabel = statusLog?.result ? RESULT_LABELS[statusLog.result] : undefined
  const counts =
    typeof statusLog?.successCount === "number" && typeof statusLog?.failCount === "number"
      ? `${statusLog.successCount} success / ${statusLog.failCount} failed`
      : undefined
  const detail = [resultLabel, counts].filter(Boolean).join(" · ")
  return (
    <div className="flex flex-col">
      <div className="flex gap-2 items-center">
        <div className="h-[6px] w-[120px] rounded-[3px] bg-[#F3F3F3] overflow-hidden">
          <div className="h-full" style={{ width: `${percent}%`, background: color || "#111" }} />
        </div>
        <KSpan text={`${percent}%`} fontWeight={color ? 700 : 400} color={color} />
      </div>
      {detail && <KSpan fontSize={12} fontWeight={500} text={detail} color={color} />}
    </div>
  )
}

const AdminTakedownStatusTimelineModal = ({
  selectedUserTakedown,
  handleClose
}: {
  selectedUserTakedown?: UserTakedown
  handleClose: () => void
}) => {
  const [statusLogList, setStatusLogList] = useState<UserTakedownStatusLog[]>([])

  useEffect(() => {
    setStatusLogList([])
    if (!selectedUserTakedown) {
      return
    }
    // Ignore a slow answer for a takedown the modal no longer shows
    let isCurrent = true
    users(`/user_takedown_status_logs/?user_takedown_id=${selectedUserTakedown.id}`, "GET")
      .then((resp) => {
        if (isCurrent) setStatusLogList(mapListToCamelCase(resp.data.results))
      })
      .catch(() => {
        if (isCurrent) toast.error("Failed to load takedown timeline", errorFilledToastOptions)
      })
    return () => {
      isCurrent = false
    }
  }, [selectedUserTakedown])

  return (
    <Dialog open={!!selectedUserTakedown} onClose={() => handleClose()}>
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d]">
        <div
          className="p-6 shadow-md rounded-[10px] bg-[#fff] w-[900px] max-w-[95vw]"
          style={{ maxHeight: "90vh", overflowY: "auto" }}
        >
          <div className="flex justify-between items-center mb-4">
            <KSpan
              text={`Takedown Timeline - ${selectedUserTakedown?.user.firstName || ""} ${
                selectedUserTakedown?.user.lastName || ""
              }`}
              fontSize={18}
              fontWeight={600}
              color="#111"
            />
            <KButton
              icon="/admin_icons/close.svg"
              onClick={() => handleClose()}
              background="transparent"
              shadowDisabled
              width="32px"
              height="32px"
              padding="6px"
            />
          </div>
          {statusLogList.length === 0 && <KSpan text="No status yet" />}
          {statusLogList.map((statusLog) => (
            <div
              key={`status_log_${statusLog.id}`}
              data-testid="admin-takedown-status-log-row"
              className="flex gap-4 items-center py-3"
              style={{ borderTop: "1px dashed grey" }}
            >
              <div style={{ width: "20%" }}>
                <KSpan text={getTakedownProcessStatusLabel(statusLog.status)} fontWeight={500} color="#111" />
              </div>
              <div style={{ width: "20%" }}>
                <TakedownProgressBar statusLog={statusLog} />
              </div>
              <div style={{ width: "32%" }}>
                <KSpan text={statusLog.description || "-"} color={getTakedownStatusLogColor(statusLog)} />
              </div>
              <div className="flex flex-col" style={{ width: "28%" }}>
                <KSpan text={`Start: ${formatTakedownStatusLogDate(statusLog.startDate)}`} fontSize={12} />
                <KSpan text={`End: ${formatTakedownStatusLogDate(statusLog.endDate)}`} fontSize={12} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  )
}

export default AdminTakedownStatusTimelineModal
